import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Briefcase, Coins, Clock, CheckCircle, ArrowRight, Plus,
  Building2, ExternalLink, AlertCircle, FileText, DollarSign, Zap,
  Send, X, ThumbsUp, ThumbsDown, Link as LinkIcon, MessageSquare
} from "lucide-react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AddExecutedTaskForm from "../components/AddExecutedTaskForm";

const CATEGORIES = ["customer_growth", "hiring", "automation", "operations", "finance", "branding", "web_digital", "local_business", "personal"];
const INDUSTRIES = ["health", "real_estate", "restaurant", "salon", "retail", "tech", "consulting", "education", "logistics", "finance_industry"];

const TAB_STYLE = (active) => ({
  padding: "0.5rem 1.2rem", borderRadius: 8, fontSize: "0.82rem", fontWeight: active ? 600 : 400,
  color: active ? "var(--accent-text)" : "var(--text-muted)", background: active ? "var(--accent-subtle)" : "transparent",
  border: active ? "1px solid rgba(59,130,246,0.1)" : "1px solid transparent",
  cursor: "pointer", fontFamily: "'Satoshi', sans-serif", transition: "all 200ms",
});

/* ── Submit Work Modal ── */
function SubmitWorkModal({ claim, onClose, onSubmitted }) {
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim()) { setError("Describe what you did"); return; }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post(`/api/orders/claims/${claim.id}/submit`, {
        submission_text: text.trim(),
        submission_url: url.trim() || null,
      });
      onSubmitted(data);
    } catch (err) { setError(formatError(err)); }
    finally { setLoading(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.3)", backdropFilter: "blur(4px)" }}
      onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="glass-card" style={{ width: "100%", maxWidth: 520, padding: "2rem", margin: "0 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h3 style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "1.1rem" }}>Submit your work</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={18} /></button>
        </div>
        <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", color: "var(--text-muted)", marginBottom: 16 }}>
          For: <strong style={{ color: "var(--text-main)" }}>{claim.order?.title || "Task"}</strong>
        </p>
        {error && (
          <div style={{ display: "flex", gap: 8, background: "rgba(255,51,102,0.06)", border: "1px solid rgba(255,51,102,0.15)", borderRadius: 10, padding: "0.6rem 0.8rem", marginBottom: 14 }}>
            <AlertCircle size={13} color="var(--pink)" />
            <span style={{ fontSize: "0.78rem", color: "var(--pink)", fontFamily: "'Satoshi', sans-serif" }}>{error}</span>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 14 }}>
            <label className="form-label">What did you accomplish? *</label>
            <textarea data-testid="submit-work-text" className="form-input" value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Describe the work you completed, results achieved, and any relevant details..."
              rows={4} style={{ resize: "vertical" }} required />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label className="form-label">Proof / link (optional)</label>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <LinkIcon size={14} color="var(--text-muted)" />
              <input data-testid="submit-work-url" className="form-input" value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..." style={{ flex: 1 }} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" className="btn-secondary" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
            <button type="submit" data-testid="submit-work-btn" className="btn-primary" disabled={loading}
              style={{ flex: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: loading ? 0.5 : 1 }}>
              <Send size={13} /> {loading ? "Submitting..." : "Submit work"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

/* ── Review Submission Modal ── */
function ReviewModal({ claim, order, onClose, onReviewed }) {
  const [feedback, setFeedback] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleReview = async (approved) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post(`/api/orders/claims/${claim.id}/review`, {
        approved,
        feedback: feedback.trim(),
      });
      onReviewed(data);
    } catch (err) { setError(formatError(err)); }
    finally { setLoading(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.3)", backdropFilter: "blur(4px)" }}
      onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="glass-card" style={{ width: "100%", maxWidth: 560, padding: "2rem", margin: "0 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "1.1rem" }}>Review submission</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={18} /></button>
        </div>
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.72rem", color: "var(--text-muted)", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>Submitted by</p>
          <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.92rem", fontWeight: 600, color: "var(--text-main)" }}>{claim.claimer_name}</p>
        </div>
        <div style={{ background: "var(--bg-soft)", borderRadius: 12, padding: "1rem 1.2rem", marginBottom: 16 }}>
          <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.72rem", color: "var(--text-muted)", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>Their work</p>
          <p data-testid="review-submission-text" style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.88rem", color: "var(--text-main)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
            {claim.submission_text}
          </p>
          {claim.submission_url && (
            <a href={claim.submission_url} target="_blank" rel="noopener noreferrer" data-testid="review-submission-url"
              style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8, fontSize: "0.78rem", color: "var(--accent)", fontFamily: "'Satoshi', sans-serif" }}>
              <LinkIcon size={12} /> {claim.submission_url}
            </a>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16, padding: "0.6rem 1rem", background: "var(--accent-subtle)", borderRadius: 10, border: "1px solid rgba(59,130,246,0.1)" }}>
          <Coins size={14} color="var(--accent)" />
          <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", color: "var(--text-secondary)" }}>
            Approving releases <strong style={{ color: "var(--accent-text)" }}>{order?.credit_reward || 0}</strong> credits to the claimer
          </span>
        </div>
        {error && (
          <div style={{ display: "flex", gap: 8, background: "rgba(255,51,102,0.06)", border: "1px solid rgba(255,51,102,0.15)", borderRadius: 10, padding: "0.6rem 0.8rem", marginBottom: 14 }}>
            <AlertCircle size={13} color="var(--pink)" />
            <span style={{ fontSize: "0.78rem", color: "var(--pink)", fontFamily: "'Satoshi', sans-serif" }}>{error}</span>
          </div>
        )}
        <div style={{ marginBottom: 16 }}>
          <label className="form-label">Feedback (optional)</label>
          <textarea data-testid="review-feedback" className="form-input" value={feedback} onChange={(e) => setFeedback(e.target.value)}
            placeholder="Share feedback with the claimer..." rows={2} style={{ resize: "vertical" }} />
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button data-testid="review-reject-btn" onClick={() => handleReview(false)} className="btn-secondary" disabled={loading}
            style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, color: "var(--pink)", borderColor: "rgba(255,51,102,0.2)" }}>
            <ThumbsDown size={13} /> Reject
          </button>
          <button data-testid="review-approve-btn" onClick={() => handleReview(true)} className="btn-primary" disabled={loading}
            style={{ flex: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: loading ? 0.5 : 1 }}>
            <ThumbsUp size={13} /> {loading ? "Processing..." : "Approve & pay"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function MyWorkPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("claims");
  const [claims, setClaims] = useState([]);
  const [postedTasks, setPostedTasks] = useState([]);
  const [business, setBusiness] = useState(null);
  const [records, setRecords] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBizForm, setShowBizForm] = useState(false);
  const [showAddRecord, setShowAddRecord] = useState(false);
  const [bizForm, setBizForm] = useState({ business_name: "", business_type: "agency", description: "", city: "", country: "", website: "", industries_served: [], task_categories: [] });
  const [bizError, setBizError] = useState("");
  const [bizLoading, setBizLoading] = useState(false);
  const [wsPosted, setWsPosted] = useState([]);
  const [wsDoer, setWsDoer] = useState([]);
  const [reviewOrder, setReviewOrder] = useState(null);

  const [submitClaim, setSubmitClaim] = useState(null);
  const [reviewClaim, setReviewClaim] = useState(null);
  useEffect(() => {
    if (user === null) navigate("/login");
    if (user) loadData();
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [claimsRes, tasksRes, bizRes, walletRes, wsPostedRes, wsDoerRes] = await Promise.all([
        api.get("/api/orders/my-claims").catch(() => ({ data: [] })),
        api.get("/api/orders/my-tasks").catch(() => ({ data: { orders: [] } })),
        api.get("/api/businesses/me").catch(() => ({ data: null })),
        api.get("/api/wallet/balance").catch(() => ({ data: null })),
        api.get("/api/workspace/my-tasks").catch(() => ({ data: [] })),
        api.get("/api/workspace/doer-tasks").catch(() => ({ data: [] })),
      ]);
      setClaims(claimsRes.data || []);
      setPostedTasks(tasksRes.data.orders || tasksRes.data || []);
      setWsPosted(wsPostedRes.data || []);
      setWsDoer(wsDoerRes.data || []);
      setBusiness(bizRes.data);
      setWallet(walletRes.data);
      if (bizRes.data) {
        const recsRes = await api.get(`/api/records/?business_id=${bizRes.data.id}`).catch(() => ({ data: [] }));
        setRecords(recsRes.data || []);
      }
    } catch (_) {}
    setLoading(false);
  };

  const createBusiness = async (e) => {
    e.preventDefault();
    setBizError("");
    setBizLoading(true);
    try {
      const { data } = await api.post("/api/businesses/", bizForm);
      setBusiness(data);
      setShowBizForm(false);
    } catch (err) {
      setBizError(formatError(err));
    } finally {
      setBizLoading(false);
    }
  };

  const toggleMultiSelect = (key, val) => {
    setBizForm(f => ({
      ...f,
      [key]: f[key].includes(val) ? f[key].filter(v => v !== val) : [...f[key], val]
    }));
  };

  const handleWorkSubmitted = (updatedClaim) => {
    setClaims(prev => prev.map(c => c.id === updatedClaim.id ? { ...c, ...updatedClaim, order: c.order } : c));
    setSubmitClaim(null);
  };

  const handleReviewed = (updatedClaim) => {
    setPostedTasks(prev => prev.map(order => ({
      ...order,
      claims: order.claims?.map(c => c.id === updatedClaim.id ? { ...c, ...updatedClaim } : c),
    })));
    setReviewClaim(null);
    setReviewOrder(null);
    loadData();
  };

  if (user === undefined || loading) {
    return <div style={{ paddingTop: 100, textAlign: "center" }}><div style={{ width: 40, height: 40, border: "3px solid rgba(59,130,246,0.1)", borderTop: "3px solid var(--accent)", borderRadius: "50%", margin: "0 auto", animation: "spin 0.8s linear infinite" }} /><style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style></div>;
  }

  const statusColors = { in_progress: "var(--primary)", submitted: "var(--violet)", approved: "var(--primary)", rejected: "var(--pink)" };

  return (
    <div style={{ paddingTop: 60, minHeight: "100vh" }}>
      {/* Submit Work Modal */}
      <AnimatePresence>
        {submitClaim && <SubmitWorkModal claim={submitClaim} onClose={() => setSubmitClaim(null)} onSubmitted={handleWorkSubmitted} />}
      </AnimatePresence>

      {/* Review Modal */}
      <AnimatePresence>
        {reviewClaim && <ReviewModal claim={reviewClaim} order={reviewOrder} onClose={() => { setReviewClaim(null); setReviewOrder(null); }} onReviewed={handleReviewed} />}
      </AnimatePresence>

      {/* Header */}
      <section style={{ position: "relative", padding: "90px 24px 28px", overflow: "hidden" }}>
        <div className="bg-blob" style={{ width: 300, height: 300, background: "rgba(59,130,246,0.04)", top: "-5%", right: "10%", animationDelay: "1s" }} />
        <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 2 }}>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.7rem", fontWeight: 600, color: "var(--accent)", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: "0.8rem" }}>Dashboard</p>
            <h1 data-testid="mywork-page-title" style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "clamp(1.8rem, 4vw, 2.4rem)", fontWeight: 300, color: "var(--text-main)", letterSpacing: "-0.04em", marginBottom: 8 }}>
              My <span style={{ fontWeight: 700 }}>Work</span>
            </h1>
            <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.9rem", color: "var(--text-secondary)", maxWidth: 460 }}>
              Your tasks, earnings, executions, and profile — all in one place.
            </p>

            {/* Quick stats */}
            {wallet && (
              <div style={{ display: "flex", gap: 20, marginTop: 20, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Coins size={15} color="var(--accent)" strokeWidth={1.5} />
                  <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", color: "var(--text-secondary)" }}>
                    <strong>{wallet.balance}</strong> credits
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <CheckCircle size={15} color="var(--success)" strokeWidth={1.5} />
                  <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", color: "var(--text-secondary)" }}>
                    <strong>{wallet.earned_total}</strong> earned
                  </span>
                </div>
                {wallet.pending_earnings > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Clock size={15} color="var(--accent)" strokeWidth={1.5} />
                    <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", color: "var(--text-secondary)" }}>
                      <strong>{wallet.pending_earnings}</strong> pending
                    </span>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Tabs */}
      <section style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px 16px" }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button data-testid="tab-claims" onClick={() => setTab("claims")} style={TAB_STYLE(tab === "claims")}>
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}><Briefcase size={14} />My Claims {claims.length > 0 && `(${claims.length})`}</span>
          </button>
          <button data-testid="tab-posted" onClick={() => setTab("posted")} style={TAB_STYLE(tab === "posted")}>
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}><FileText size={14} />Posted Tasks {postedTasks.length > 0 && `(${postedTasks.length})`}</span>
          </button>
          <button data-testid="tab-profile" onClick={() => setTab("profile")} style={TAB_STYLE(tab === "profile")}>
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}><Building2 size={14} />Execution Profile</span>
          </button>
        </div>
      </section>

      {/* Content */}
      <section style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px 80px" }}>
        {/* === CLAIMS TAB === */}
        {tab === "claims" && (
          <div>
            {claims.length === 0 ? (
              <div className="glass-card" style={{ padding: "3rem", textAlign: "center" }}>
                <Briefcase size={32} color="var(--bg-neutral)" style={{ marginBottom: 12 }} />
                <p style={{ fontFamily: "'Satoshi', sans-serif", color: "var(--text-muted)", marginBottom: 16 }}>You haven't claimed any tasks yet.</p>
                <Link to="/earn" className="btn-primary" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Coins size={14} />Browse tasks to earn
                </Link>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {claims.map((claim, i) => {
                  const order = claim.order;
                  return (
                    <motion.div key={claim.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                      data-testid={`claim-card-${i}`} className="glass-card" style={{ padding: "20px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                        <div style={{ flex: 1 }}>
                          <h3 style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "1rem", color: "var(--text-main)", marginBottom: 4 }}>
                            {order?.title || "Task"}
                          </h3>
                          <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", color: "var(--text-muted)", marginBottom: 8 }}>
                            {order?.description?.slice(0, 120)}...
                          </p>
                          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: 4, fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem", color: "var(--text-muted)" }}>
                              <Coins size={12} color="var(--accent)" strokeWidth={1.5} /> {order?.credit_reward || 0} credits
                            </span>
                            <span style={{
                              padding: "2px 10px", borderRadius: 100, fontSize: "0.72rem", fontWeight: 600,
                              fontFamily: "'Satoshi', sans-serif", textTransform: "uppercase",
                              color: statusColors[claim.status] || "var(--text-muted)",
                              background: `${statusColors[claim.status] || "var(--text-muted)"}15`,
                              border: `1px solid ${statusColors[claim.status] || "var(--text-muted)"}30`,
                            }}>
                              {claim.status?.replace(/_/g, " ")}
                            </span>
                            {claim.status === "submitted" && (
                              <span style={{ fontSize: "0.72rem", color: "var(--violet)", fontFamily: "'Satoshi', sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
                                <Clock size={11} /> Awaiting review
                              </span>
                            )}
                            {claim.status === "approved" && claim.feedback && (
                              <span style={{ fontSize: "0.72rem", color: "var(--success)", fontFamily: "'Satoshi', sans-serif", display: "flex", alignItems: "center", gap: 4 }}>
                                <MessageSquare size={11} /> {claim.feedback}
                              </span>
                            )}
                          </div>
                        </div>
                        {claim.status === "in_progress" && (
                          <button data-testid={`submit-work-trigger-${i}`} className="btn-primary" onClick={() => setSubmitClaim(claim)}
                            style={{ fontSize: "0.78rem", padding: "0.4rem 1rem", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 5 }}>
                            <Send size={12} /> Submit Work
                          </button>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* === POSTED TASKS TAB === */}
        {tab === "posted" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.85rem", color: "var(--text-muted)" }}>Tasks you've posted for others to execute.</p>
              <Link to="/post-task" data-testid="post-task-link" className="btn-primary" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.82rem" }}>
                <Plus size={14} />Post a task
              </Link>
            </div>
            {postedTasks.length === 0 ? (
              <div className="glass-card" style={{ padding: "3rem", textAlign: "center" }}>
                <FileText size={32} color="var(--bg-neutral)" style={{ marginBottom: 12 }} />
                <p style={{ fontFamily: "'Satoshi', sans-serif", color: "var(--text-muted)", marginBottom: 16 }}>You haven't posted any tasks yet.</p>
                <Link to="/post-task" className="btn-primary" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Plus size={14} />Post your first task
                </Link>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {postedTasks.map((order, i) => (
                  <motion.div key={order.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    data-testid={`posted-task-${i}`} className="glass-card" style={{ padding: "20px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <h3 style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "1rem", marginBottom: 4 }}>{order.title}</h3>
                        <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.82rem", color: "var(--text-muted)", marginBottom: 8 }}>
                          {order.description?.slice(0, 100)}...
                        </p>
                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                          <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 4 }}>
                            <Coins size={12} color="var(--accent)" strokeWidth={1.5} /> {order.credit_reward} credits
                          </span>
                          <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem", color: "var(--text-muted)" }}>
                            {order.claims_count}/{order.max_claims} claimed
                          </span>
                          <span style={{
                            padding: "2px 8px", borderRadius: 100, fontSize: "0.7rem", fontWeight: 600,
                            fontFamily: "'Satoshi', sans-serif",
                            color: order.status === "open" ? "var(--success)" : "var(--accent)",
                            background: order.status === "open" ? "var(--accent-subtle)" : "var(--accent-subtle)",
                          }}>
                            {order.status}
                          </span>
                        </div>
                      </div>
                    </div>
                    {order.claims?.length > 0 && (
                      <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(226,232,240,0.5)" }}>
                        <p style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.72rem", color: "var(--text-muted)", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>
                          Claims ({order.claims.length})
                        </p>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {order.claims.map((c, j) => (
                            <div key={c.id} data-testid={`claim-review-${i}-${j}`} style={{
                              display: "flex", justifyContent: "space-between", alignItems: "center",
                              padding: "10px 14px", borderRadius: 10, background: "var(--bg-soft)",
                            }}>
                              <div>
                                <span style={{ fontFamily: "'Satoshi', sans-serif", fontSize: "0.85rem", fontWeight: 500, color: "var(--text-main)" }}>
                                  {c.claimer_name}
                                </span>
                                <span style={{
                                  marginLeft: 10, padding: "2px 8px", borderRadius: 100, fontSize: "0.68rem", fontWeight: 600,
                                  fontFamily: "'Satoshi', sans-serif", textTransform: "uppercase",
                                  color: statusColors[c.status] || "var(--text-muted)",
                                  background: `${statusColors[c.status] || "var(--text-muted)"}10`,
                                }}>
                                  {c.status?.replace(/_/g, " ")}
                                </span>
                              </div>
                              {c.status === "submitted" && (
                                <button data-testid={`review-trigger-${i}-${j}`} onClick={() => { setReviewClaim(c); setReviewOrder(order); }}
                                  className="btn-accent" style={{ fontSize: "0.75rem", padding: "0.3rem 0.8rem", display: "flex", alignItems: "center", gap: 4 }}>
                                  <CheckCircle size={12} /> Review
                                </button>
                              )}
                              {c.status === "approved" && (
                                <span style={{ fontSize: "0.75rem", color: "var(--success)", fontFamily: "'Satoshi', sans-serif", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                                  <CheckCircle size={12} /> Paid
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* === EXECUTION PROFILE TAB === */}
        {tab === "profile" && (
          <div>
            {!business && !showBizForm ? (
              <div className="glass-card" style={{ padding: "3rem", textAlign: "center" }}>
                <Building2 size={36} color="var(--bg-neutral)" style={{ marginBottom: 12 }} />
                <h3 style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "1.2rem", marginBottom: 8 }}>Build your execution profile</h3>
                <p style={{ fontFamily: "'Satoshi', sans-serif", color: "var(--text-muted)", marginBottom: 20, maxWidth: 400, margin: "0 auto 20px" }}>
                  Document your successes, build trust, and get matched to tasks. Create a business profile to start.
                </p>
                <button data-testid="create-business-btn" className="btn-primary" onClick={() => setShowBizForm(true)} style={{ padding: "0.7rem 1.8rem" }}>
                  Create execution profile
                </button>
              </div>
            ) : !business && showBizForm ? (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
                <h2 style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "1.2rem", marginBottom: 16 }}>Your execution profile</h2>
                {bizError && (
                  <div style={{ display: "flex", gap: 8, background: "var(--pink-subtle)", border: "1px solid rgba(255,51,102,0.15)", borderRadius: 10, padding: "0.7rem 0.9rem", marginBottom: "1.2rem" }}>
                    <AlertCircle size={15} color="var(--pink)" />
                    <span style={{ fontSize: "0.82rem", color: "var(--pink)", fontFamily: "'Satoshi', sans-serif" }}>{bizError}</span>
                  </div>
                )}
                <div className="glass-card" style={{ padding: "2rem" }}>
                  <form onSubmit={createBusiness}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: "1rem" }}>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <label className="form-label">Business / profile name *</label>
                        <input data-testid="biz-name-input" className="form-input" value={bizForm.business_name} onChange={e => setBizForm(f => ({ ...f, business_name: e.target.value }))} placeholder="Your name or business name" required />
                      </div>
                      <div>
                        <label className="form-label">Type</label>
                        <select data-testid="biz-type-select" className="form-input" value={bizForm.business_type} onChange={e => setBizForm(f => ({ ...f, business_type: e.target.value }))}>
                          <option value="agency">Agency</option>
                          <option value="consultancy">Consultancy</option>
                          <option value="freelancer">Freelancer</option>
                          <option value="company">Company</option>
                        </select>
                      </div>
                      <div>
                        <label className="form-label">Website</label>
                        <input data-testid="biz-website-input" className="form-input" value={bizForm.website} onChange={e => setBizForm(f => ({ ...f, website: e.target.value }))} placeholder="https://..." />
                      </div>
                      <div>
                        <label className="form-label">City *</label>
                        <input data-testid="biz-city-input" className="form-input" value={bizForm.city} onChange={e => setBizForm(f => ({ ...f, city: e.target.value }))} placeholder="e.g. London" required />
                      </div>
                      <div>
                        <label className="form-label">Country *</label>
                        <input data-testid="biz-country-input" className="form-input" value={bizForm.country} onChange={e => setBizForm(f => ({ ...f, country: e.target.value }))} placeholder="e.g. UK" required />
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <label className="form-label">Description *</label>
                        <textarea data-testid="biz-description-input" className="form-input" value={bizForm.description} onChange={e => setBizForm(f => ({ ...f, description: e.target.value }))} placeholder="What do you do best?" required rows={3} style={{ resize: "vertical" }} />
                      </div>
                    </div>
                    <div style={{ marginBottom: "1rem" }}>
                      <label className="form-label">Task categories</label>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                        {CATEGORIES.map(cat => (
                          <button key={cat} type="button" onClick={() => toggleMultiSelect("task_categories", cat)}
                            style={{ padding: "0.3rem 0.7rem", borderRadius: 100, fontSize: "0.75rem", fontWeight: 500, border: `1.5px solid ${bizForm.task_categories.includes(cat) ? "rgba(59,130,246,0.15)" : "var(--bg-neutral)"}`, background: bizForm.task_categories.includes(cat) ? "var(--accent-subtle)" : "rgba(255,255,255,0.8)", color: bizForm.task_categories.includes(cat) ? "var(--accent-text)" : "var(--text-secondary)", cursor: "pointer", fontFamily: "'Satoshi', sans-serif" }}>
                            {cat.replace(/_/g, " ")}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ marginBottom: "1.5rem" }}>
                      <label className="form-label">Industries</label>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                        {INDUSTRIES.map(ind => (
                          <button key={ind} type="button" onClick={() => toggleMultiSelect("industries_served", ind)}
                            style={{ padding: "0.3rem 0.7rem", borderRadius: 100, fontSize: "0.75rem", fontWeight: 500, border: `1.5px solid ${bizForm.industries_served.includes(ind) ? "rgba(0,92,238,0.15)" : "var(--bg-neutral)"}`, background: bizForm.industries_served.includes(ind) ? "var(--accent-subtle)" : "rgba(255,255,255,0.8)", color: bizForm.industries_served.includes(ind) ? "var(--primary)" : "var(--text-secondary)", cursor: "pointer", fontFamily: "'Satoshi', sans-serif" }}>
                            {ind.replace(/_/g, " ")}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                      <button type="button" className="btn-secondary" onClick={() => setShowBizForm(false)} style={{ flex: 1 }}>Cancel</button>
                      <button type="submit" data-testid="biz-submit-btn" className="btn-primary" disabled={bizLoading} style={{ flex: 2, padding: "0.7rem", borderRadius: 12, opacity: bizLoading ? 0.7 : 1 }}>
                        {bizLoading ? "Creating..." : "Create profile"}
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
            ) : (
              /* Business Profile Dashboard */
              <div>
                <div className="glass-card" data-testid="business-dashboard-header" style={{ padding: "1.8rem", marginBottom: "2rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
                    <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                      <div style={{ width: 52, height: 52, borderRadius: 14, background: "var(--accent-subtle)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Building2 size={22} color="var(--accent)" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h2 style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 800, fontSize: "1.3rem", letterSpacing: "-0.02em", marginBottom: "0.2rem" }}>{business.business_name}</h2>
                        <p style={{ color: "var(--text-muted)", fontSize: "0.83rem", fontFamily: "'Satoshi', sans-serif" }}>{business.city}, {business.country} · {business.business_type}</p>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <div style={{ textAlign: "center", padding: "0.5rem 1rem", background: "var(--accent-subtle)", borderRadius: 10, border: "1px solid rgba(59,130,246,0.1)" }}>
                        <div style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 800, fontSize: "1.3rem", color: "var(--accent-text)" }}>{business.trust_score}</div>
                        <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif" }}>Trust</div>
                      </div>
                      <div style={{ textAlign: "center", padding: "0.5rem 1rem", background: "var(--violet-subtle)", borderRadius: 10, border: "1px solid rgba(138,79,255,0.15)" }}>
                        <div style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 800, fontSize: "1.3rem", color: "var(--success)" }}>{records.length}</div>
                        <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", fontFamily: "'Satoshi', sans-serif" }}>Records</div>
                      </div>
                      <a href={`/business/${business.slug}`} data-testid="view-public-profile-btn" style={{ display: "flex", alignItems: "center", gap: 4, padding: "0.5rem 0.9rem", borderRadius: 8, background: "rgba(255,255,255,0.8)", border: "1px solid rgba(0,0,0,0.08)", textDecoration: "none", fontSize: "0.78rem", color: "var(--text-secondary)", fontFamily: "'Satoshi', sans-serif" }}>
                        <ExternalLink size={12} /> View profile
                      </a>
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <h3 style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "1.05rem" }}>Execution records</h3>
                  <button data-testid="add-record-btn" className="btn-primary" onClick={() => setShowAddRecord(true)} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.82rem" }}>
                    <Plus size={14} /> Document a success
                  </button>
                </div>

                {showAddRecord && (
                  <AddExecutedTaskForm businessId={business.id} onSuccess={(rec) => { setRecords(r => [rec, ...r]); setShowAddRecord(false); }} onCancel={() => setShowAddRecord(false)} />
                )}

                {records.length === 0 ? (
                  <div className="glass-card" style={{ padding: "2.5rem", textAlign: "center" }}>
                    <Zap size={28} color="var(--bg-neutral)" style={{ marginBottom: 8 }} />
                    <p style={{ fontFamily: "'Satoshi', sans-serif", color: "var(--text-muted)" }}>No execution records yet.</p>
                  </div>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 }}>
                    {records.map((rec, i) => (
                      <motion.div key={rec.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                        <div data-testid={`record-card-${i}`} className="glass-card" style={{ padding: "1.3rem" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.7rem" }}>
                            <div style={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: "0.9rem", flex: 1, paddingRight: 8 }}>{rec.title}</div>
                            {rec.verification_status === "verified" ? (
                              <span className="badge-verified">Verified</span>
                            ) : (
                              <span style={{ fontSize: "0.68rem", color: "var(--accent)", fontWeight: 600, fontFamily: "'Satoshi', sans-serif" }}>Pending</span>
                            )}
                          </div>
                          <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", lineHeight: 1.5, fontFamily: "'Satoshi', sans-serif", marginBottom: "0.8rem" }}>{rec.narrative_summary?.slice(0, 110)}...</p>
                          <div style={{ display: "flex", gap: 12 }}>
                            <span style={{ fontSize: "0.72rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 3, fontFamily: "'Satoshi', sans-serif" }}>
                              <Clock size={10} /> {rec.time_taken_days}d
                            </span>
                            <span style={{ fontSize: "0.72rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 3, fontFamily: "'Satoshi', sans-serif" }}>
                              <DollarSign size={10} /> ${rec.budget_min?.toLocaleString()}–${rec.budget_max?.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
